import React, { useMemo, useState } from 'react';
import { IoEllipsisVertical } from 'react-icons/io5';

import { Button } from 'src/components/Button';
import { SearchInput } from 'src/components/SearchInput';
import { Table } from 'src/components/TableNew';
import { useDispatch } from 'src/store/useDispatch';
import { setAppAlert } from 'src/store/slice/appAlert';
import { UserSummary } from 'src/api/adminUsers';

import {
  ActionIconButton,
  CompactSelect,
  DonutChart,
  DonutWrap,
  LegendDot,
  LegendItem,
  LegendList,
  OverviewCard,
  OverviewCardTitle,
  OverviewGrid,
  OverviewItem,
  OverviewLabel,
  OverviewValue,
  PortfolioLayout,
  ProgressFill,
  ProgressTrack,
  SearchControl,
  SectionControls,
  SectionHint,
  SectionTitle,
  SectionToolbar,
  TabPanel,
} from './styled';

type PortfolioTabProps = {
  summary: UserSummary;
};

const COLORS = ['#3B82F6', '#22C55E', '#F59E0B', '#14B8A6', '#8B5CF6', '#64748B', '#94A3B8'];

const PortfolioTab: React.FC<PortfolioTabProps> = ({ summary }) => {
  const dispatch = useDispatch();
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('top');

  const holdings = useMemo(() => summary.balance.holdings ?? [], [summary]);
  const unpriced = useMemo(() => summary.balance.unpriced ?? [], [summary]);

  const unpricedRows = useMemo(
    () =>
      unpriced.map((item, index) => {
        if (typeof item === 'string') {
          return { id: `${item}-${index}`, asset: item, balance: '—' };
        }
        return {
          id: `${item.currency_code}-${index}`,
          asset: item.currency_code,
          balance: item.balance || '—',
        };
      }),
    [unpriced],
  );

  const chartData = useMemo(() => {
    const total = holdings.reduce((sum, h) => sum + (Number(h.value) || 0), 0) || 1;
    const mapped = holdings.map((h, i) => ({
      code: h.currency_code,
      pct: Math.round(((Number(h.value) || 0) / total) * 100),
      color: COLORS[i % COLORS.length],
      balance: h.balance,
      value: h.value,
    }));
    if (!mapped.length) {
      return [{ code: 'Others', pct: 100, color: COLORS[6], balance: '0', value: '0' }];
    }
    return mapped;
  }, [holdings]);

  const stops = chartData
    .reduce<{ parts: string[]; acc: number }>(
      (state, item) => {
        const start = state.acc;
        const end = state.acc + item.pct;
        state.parts.push(`${item.color} ${start}% ${end}%`);
        state.acc = end;
        return state;
      },
      { parts: [], acc: 0 },
    )
    .parts.join(', ');

  const rows = holdings
    .filter((h) => {
      if (!search.trim()) return true;
      return h.currency_code.toLowerCase().includes(search.trim().toLowerCase());
    })
    .map((h, i) => ({
      id: h.currency_code,
      asset: h.currency_code,
      type: 'Crypto',
      balance: h.balance,
      fiat: h.value != null ? `$${h.value}` : '—',
      avg_buy: '—',
      pct: `${chartData[i]?.pct ?? 0}%`,
    }));

  const notifyComingSoon = (feature: string) => {
    dispatch(
      setAppAlert({
        message: `${feature} is not available yet — API pending.`,
        isSuccess: false,
      }),
    );
  };

  return (
    <TabPanel>
      <SectionToolbar>
        <div>
          <SectionTitle>Portfolio Distribution</SectionTitle>
          <SectionHint style={{ marginBottom: 0 }}>
            <strong style={{ fontSize: '1.25rem', color: 'inherit' }}>{holdings.length || 0}</strong>
            <br />
            Asset Holdings
          </SectionHint>
        </div>
        <SectionControls>
          <SearchControl>
            <SearchInput
              inline
              showIcon
              placeholder="Search..."
              value={search}
              onChange={setSearch}
            />
          </SearchControl>
          <CompactSelect value={sort} onChange={(e) => setSort(e.target.value)}>
            <option value="top">Top Holdings</option>
            <option value="all">All Holdings</option>
          </CompactSelect>
          <Button text="View All" className="black" onClick={() => notifyComingSoon('View all portfolio')} />
        </SectionControls>
      </SectionToolbar>

      <PortfolioLayout>
        <DonutWrap>
          <DonutChart $stops={stops || `${COLORS[6]} 0% 100%`} />
          <LegendList>
            {chartData.map((item) => (
              <LegendItem key={item.code}>
                <LegendDot $color={item.color} />
                <span>
                  {item.code} {item.pct}%
                </span>
              </LegendItem>
            ))}
          </LegendList>
        </DonutWrap>

        <div>
          {rows.length ? (
            <Table
              headers={['Asset', 'Type', 'Balance', 'Fiat Value', 'Avg. Buy', '%']}
              items={rows}
              columns="1.2fr 0.8fr 1fr 1fr 0.9fr 0.6fr"
              fieldsToShow={['asset', 'type', 'balance', 'fiat', 'avg_buy', 'pct']}
              totalPages={1}
              currentPage={1}
              setCurrentPage={() => undefined}
              disableRowDetails
              actions={() => (
                <ActionIconButton
                  type="button"
                  aria-label="Asset actions"
                  onClick={() => notifyComingSoon('Asset actions')}
                >
                  <IoEllipsisVertical size={16} />
                </ActionIconButton>
              )}
            />
          ) : (
            <SectionHint>No priced holdings to display.</SectionHint>
          )}

          {unpricedRows.length > 0 && (
            <div style={{ marginTop: '1.25rem' }}>
              <SectionTitle>Unpriced holdings</SectionTitle>
              <SectionHint>
                These balances have no live rate and are excluded from the total above.
              </SectionHint>
              <Table
                headers={['Asset', 'Balance']}
                items={unpricedRows}
                columns="1fr 1fr"
                fieldsToShow={['asset', 'balance']}
                totalPages={1}
                currentPage={1}
                setCurrentPage={() => undefined}
                disableRowDetails
              />
            </div>
          )}
        </div>
      </PortfolioLayout>

      <OverviewGrid style={{ marginTop: '1.25rem' }}>
        <OverviewCard>
          <OverviewCardTitle>Yield & Staking Overview</OverviewCardTitle>
          <OverviewItem>
            <OverviewLabel>Total Staked</OverviewLabel>
            <OverviewValue>—</OverviewValue>
          </OverviewItem>
          <OverviewItem>
            <OverviewLabel>Accrued Interest</OverviewLabel>
            <OverviewValue>—</OverviewValue>
          </OverviewItem>
          <OverviewItem>
            <OverviewLabel>Next Payout</OverviewLabel>
            <OverviewValue>—</OverviewValue>
          </OverviewItem>
          <OverviewItem>
            <OverviewLabel>Vesting Progress</OverviewLabel>
            <ProgressTrack>
              <ProgressFill $pct={0} />
            </ProgressTrack>
          </OverviewItem>
          <SectionHint style={{ marginTop: '0.75rem', marginBottom: 0 }}>
            Placeholder — staking API pending.
          </SectionHint>
        </OverviewCard>

        <OverviewCard>
          <OverviewCardTitle>Wallet & Network Details</OverviewCardTitle>
          <OverviewItem>
            <OverviewLabel>BTC Address</OverviewLabel>
            <OverviewValue>—</OverviewValue>
          </OverviewItem>
          <OverviewItem>
            <OverviewLabel>ETH / BXT Address</OverviewLabel>
            <OverviewValue>—</OverviewValue>
          </OverviewItem>
          <OverviewItem>
            <OverviewLabel>Network Status</OverviewLabel>
            <OverviewValue>On-Chain</OverviewValue>
          </OverviewItem>
          <OverviewItem>
            <OverviewLabel>Whitelisted Addresses</OverviewLabel>
            <OverviewValue>—</OverviewValue>
          </OverviewItem>
          <SectionHint style={{ marginTop: '0.75rem', marginBottom: 0 }}>
            Placeholder — network extras pending.
          </SectionHint>
        </OverviewCard>
      </OverviewGrid>
    </TabPanel>
  );
};

export default PortfolioTab;
